/**
 * Seo — central per-route meta helper.
 *
 * Sets <title>, meta description, canonical URL plus the Open Graph /
 * Twitter Card tags that Facebook, WhatsApp, Telegram and link previews
 * use. Every page passes the bits relevant to its content; sensible
 * defaults fill the rest so the site never serves an empty share card.
 */

import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { SITE_NAME, SITE_BASE } from '@/config/site'

const SITE_TITLE_TEMPLATE = ` — ${SITE_NAME}`
const DEFAULT_DESCRIPTION =
  'Skulpturen-Portfolio von Anna Sacher Santana — österreichische Bildhauerin und Kinder- und Jugendpsychiaterin. 3D-Werke, Kunstprojekte, Ausstellungen.'
const DEFAULT_IMAGE = `${SITE_BASE}/og-default.jpg`

interface SeoProps {
  /** Page title without the site suffix. Leave empty to use the home title. */
  title?: string
  /** Short summary shown in search results + social cards (~155 chars max). */
  description?: string
  /** Absolute image URL (will be resolved if app-relative). */
  image?: string | null
  /** Override the canonical URL (defaults to current pathname under SITE_BASE). */
  url?: string
  /** og:type — "website" for hubs, "article" for individual works. */
  type?: 'website' | 'article' | 'profile'
  /** Optional JSON-LD object inlined as <script type="application/ld+json">. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

function absUrl(value: string | null | undefined): string {
  if (!value) return DEFAULT_IMAGE
  if (/^https?:\/\//i.test(value)) return value
  return SITE_BASE + (value.startsWith('/') ? value : '/' + value)
}

export function Seo({ title, description, image, url, type = 'website', jsonLd }: SeoProps) {
  const location = useLocation()
  const finalTitle = title ? title + SITE_TITLE_TEMPLATE : SITE_NAME + ' — Skulpturen Portfolio'
  const finalDescription = (description || DEFAULT_DESCRIPTION).slice(0, 300)
  const finalImage = absUrl(image)
  const finalUrl = url || SITE_BASE + location.pathname

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={finalUrl} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:locale" content="de_DE" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  )
}

/* ----------------------------------------------------------------------
 * JSON-LD builders — call from individual pages to attach structured data
 * -------------------------------------------------------------------- */

export const ANNA_PERSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'Anna Sacher Santana',
  alternateName: 'Anna Sacher',
  jobTitle: ['Bildhauerin', 'Fachärztin für Kinder- und Jugendpsychiatrie'],
  nationality: 'AT',
  url: SITE_BASE,
  image: DEFAULT_IMAGE,
  worksFor: { '@type': 'Organization', name: 'Anna Sacher Atelier' },
  knowsAbout: [
    'Bildhauerei',
    'Kunsttherapie',
    'Kinder- und Jugendpsychiatrie',
    'Psychotherapie',
    'Traumasensibles Yoga',
  ],
}

interface ArtworkLdInput {
  title: string
  description?: string
  year?: string | null
  material?: string | null
  image?: string | null
  url: string
}

export function buildArtworkLd(a: ArtworkLdInput) {
  const node: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'VisualArtwork',
    name: a.title,
    creator: { '@type': 'Person', name: 'Anna Sacher Santana' },
    artist: { '@type': 'Person', name: 'Anna Sacher Santana' },
    url: a.url,
    image: absUrl(a.image),
  }
  if (a.description) node.description = a.description.slice(0, 500)
  if (a.year) node.dateCreated = a.year
  if (a.material) node.artMedium = a.material
  return node
}

export function buildBreadcrumbLd(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  }
}
